import { DashboardSpaceIdSchema, type Space } from '@rock/types';
import { orderBy } from 'lodash';

/**
 * Spaces in the order the app itself shows them, read straight off the shared `DashboardSpaceId`
 * enum so admin can't drift from the app: home, hvac, appliances, plumbing, safety, electrical,
 * surfaces, garage.
 *
 * Spaces outside that enum — `roof`, `exterior-surfaces`, anything added to the catalog but not yet
 * on the dashboard — sort after the known ones, alphabetically, rather than disappearing or landing
 * in the middle.
 */
const DASHBOARD_ORDER = new Map<string, number>(DashboardSpaceIdSchema.options.map((id, index) => [id, index]));

/** Rank used to sort a space; unknown spaces sort last. Exported for sorting things keyed BY spaceId. */
export function spaceRank(spaceId: string): number {
    return DASHBOARD_ORDER.get(spaceId) ?? Number.MAX_SAFE_INTEGER;
}

/** Sorts spaces into the app's dashboard order. Applied once in `spacesState`, so every list inherits it. */
export function sortSpaces<T extends Pick<Space, 'id' | 'name'>>(spaces: T[]): T[] {
    return orderBy(spaces, [(space) => spaceRank(space.id), 'name']);
}

/**
 * Dashboard order, but with unpublished spaces gathered at the end. For navigation, where the live
 * spaces are what you reach for daily and a half-built draft sitting between them is a hazard — the
 * app's own order is still what the published ones are in.
 */
export function sortSpacesDraftsLast<T extends Pick<Space, 'id' | 'name' | 'published'>>(spaces: T[]): T[] {
    return orderBy(spaces, [(space) => (space.published ? 0 : 1), (space) => spaceRank(space.id), 'name']);
}
